import type { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import { linkMethods } from '@synapex/shared2/entities'
import type { MarkingEditorState, TextAlignment } from '../../types'
import type { UpdateMarkingFormDataInputDto } from '@synapex/shared2/gateway-rest-api'
import { isPartialMarkingEditorState } from '../validation/stateValidation'

/**
 * Информация о масштабе для пересчета позиций
 */
export interface MarkingEditorScale {
    canvasWidth: number
    canvasHeight: number
    originalWidth: number
    originalHeight: number
}

/**
 * Type guard для проверки, является ли значение валидным MarkingEditorScale
 * @param scale - данные для проверки
 * @returns true, если данные являются валидным MarkingEditorScale
 */
export function isValidScale(scale: unknown): scale is MarkingEditorScale {
    if (!scale || typeof scale !== 'object' || Array.isArray(scale)) {
        return false
    }

    const s = scale as Record<string, unknown>

    return (
        typeof s.canvasWidth === 'number' &&
        typeof s.canvasHeight === 'number' &&
        typeof s.originalWidth === 'number' &&
        typeof s.originalHeight === 'number' &&
        s.canvasWidth > 0 &&
        s.canvasHeight > 0 &&
        s.originalWidth > 0 &&
        s.originalHeight > 0
    )
}

/**
 * Расширенный DTO с новыми полями маркировки (SYN-829)
 * Поля уже добавлены в schema.prisma и marking-form.ts
 * Этот тип временный, пока не будут сгенерированы новые типы из shared2
 */
export interface ExtendedUpdateMarkingFormDataDto
    extends UpdateMarkingFormDataInputDto {
    mark_media_font_size?: number
    mark_media_alignment?: TextAlignment
    mark_media_font_family?: string
    mark_media_has_background?: boolean
    mark_media_background_color?: string | null
    mark_media_position_x?: number
    mark_media_position_y?: number
    // Информация о масштабе для пересчета шрифта и позиций при экспорте
    mark_media_canvas_width?: number
    mark_media_canvas_height?: number
    mark_media_original_width?: number
    mark_media_original_height?: number
}

/**
 * Генерация ключа для localStorage с настройками маркировки
 * Используется только для миграции старых данных (временное решение)
 *
 * @param wwld - дескриптор рабочего пространства и ссылки
 * @returns ключ для localStorage или null, если не удалось сгенерировать
 *
 * @example
 * ```ts
 * const key = getLocalStorageKey(wwld)
 * // "marking-editor-state-workspace_type-workspace_id-link_id"
 * ```
 */
export function getLocalStorageKey(
    wwld: WorkspaceWithLinkDescriptor
): string | null {
    try {
        const { pickLd } = linkMethods
        const ld = pickLd(wwld)

        if (
            !ld?.link_id ||
            (typeof ld.link_id !== 'string' && typeof ld.link_id !== 'number')
        ) {
            return null
        }

        return `marking-editor-state-${wwld.workspace_type}-${wwld.workspace_id}-${ld.link_id}`
    } catch (error) {
        return null
    }
}

/**
 * Загрузка расширенных настроек из localStorage (для миграции старых данных)
 * После миграции данные удаляются из localStorage
 *
 * @param wwld - дескриптор рабочего пространства и ссылки
 * @returns состояние редактора с масштабом или null, если данных нет или они повреждены
 *
 * @example
 * ```ts
 * const data = loadFromLocalStorage(wwld)
 * if (data) {
 *   // Использовать данные для инициализации
 * }
 * ```
 */
export function loadFromLocalStorage(
    wwld: WorkspaceWithLinkDescriptor
):
    | (Partial<MarkingEditorState> & { scale?: MarkingEditorScale | null })
    | null {
    try {
        const key = getLocalStorageKey(wwld)
        if (!key) {
            return null
        }

        const saved = localStorage.getItem(key)
        if (!saved) {
            return null
        }

        const parsed: unknown = JSON.parse(saved)

        // Валидация: проверяем, что parsed является объектом
        const isValidParsedData = (
            data: unknown
        ): data is Record<string, unknown> => {
            return (
                data !== null &&
                typeof data === 'object' &&
                !Array.isArray(data)
            )
        }

        if (!isValidParsedData(parsed)) {
            // Очищаем поврежденные данные
            localStorage.removeItem(key)
            return null
        }

        // Валидация структуры данных с помощью type guard
        // Проверяем только поля MarkingEditorState, scale проверяем отдельно
        const stateData: Record<string, unknown> = { ...parsed }
        delete stateData.scale // Удаляем scale для проверки state

        if (!isPartialMarkingEditorState(stateData)) {
            // Очищаем поврежденные данные
            localStorage.removeItem(key)
            return null
        }

        // Валидация scale, если он присутствует
        const result: Partial<MarkingEditorState> & {
            scale?: MarkingEditorScale | null
        } = { ...stateData } as Partial<MarkingEditorState>

        if (parsed.scale !== undefined && parsed.scale !== null) {
            if (isValidScale(parsed.scale)) {
                result.scale = parsed.scale
            } else {
                // Если scale невалиден, удаляем его
                result.scale = null
            }
        }

        return result
    } catch (error) {
        // В случае ошибки парсинга, очищаем поврежденные данные
        try {
            const key = getLocalStorageKey(wwld)
            if (key) {
                localStorage.removeItem(key)
            }
        } catch (cleanupError) {
            // Игнорируем ошибки очистки
        }
    }
    return null
}

/**
 * Удаление данных из localStorage после миграции в БД
 * Используется для очистки старых данных после успешного сохранения в БД
 *
 * @param wwld - дескриптор рабочего пространства и ссылки
 *
 * @example
 * ```ts
 * await saveToDatabase(data)
 * clearLocalStorage(wwld) // Очищаем старые данные
 * ```
 */
export function clearLocalStorage(wwld: WorkspaceWithLinkDescriptor): void {
    try {
        const key = getLocalStorageKey(wwld)
        if (key) {
            localStorage.removeItem(key)
        }
    } catch (error) {
        // Игнорируем ошибки очистки
    }
}

/**
 * Округление числа до 2 знаков после запятой
 * Используется для сохранения позиций, чтобы избежать проблем с числами с плавающей точкой
 *
 * @param value - число для округления
 * @returns округленное число (2 знака после запятой)
 *
 * @example
 * ```ts
 * roundPosition(50.123456) // 50.12
 * roundPosition(90.999) // 91
 * ```
 */
export function roundPosition(value: number): number {
    return Math.round(value * 100) / 100
}

/**
 * Создание DTO для сохранения настроек маркировки в БД
 * Преобразует состояние редактора и информацию о масштабе в формат, совместимый с API
 *
 * @param state - состояние редактора маркировки
 * @param scale - информация о масштабе canvas (опционально)
 * @returns DTO для сохранения в БД, совместимый с UpdateMarkingFormDataInputDto
 *
 * @example
 * ```ts
 * const dto = createMarkingFormDataDto(state, scale)
 * await updateMarkingFormData.mutate(dto)
 * ```
 */
export function createMarkingFormDataDto(
    state: MarkingEditorState,
    scale?: MarkingEditorScale | null,
    customTextColors?: string[],
    customBackgroundColors?: string[]
): Partial<UpdateMarkingFormDataInputDto> {
    return {
        mark_media: state.markMedia,
        mark_media_color: state.markMediaColor,
        mark_media_text: state.markMediaText ?? undefined,
        // Расширенные поля маркировки (SYN-829)
        // Сохраняем fontSize напрямую из state (уже прошел через clampFontSize)
        // Гарантируем что максимальное значение (18) сохраняется как число
        mark_media_font_size: state.fontSize,
        mark_media_alignment: state.alignment,
        mark_media_font_family: state.fontFamily,
        mark_media_has_background: state.hasBackground,
        mark_media_background_color: state.backgroundColor ?? undefined,
        mark_media_position_x:
            state.position?.x !== undefined
                ? roundPosition(state.position.x)
                : undefined,
        mark_media_position_y:
            state.position?.y !== undefined
                ? roundPosition(state.position.y)
                : undefined,
        // Информация о масштабе для пересчета шрифта и позиций при экспорте
        // Округляем до целых чисел, так как валидация требует @IsInt()
        mark_media_canvas_width: scale?.canvasWidth
            ? Math.round(scale.canvasWidth)
            : undefined,
        mark_media_canvas_height: scale?.canvasHeight
            ? Math.round(scale.canvasHeight)
            : undefined,
        mark_media_original_width: scale?.originalWidth
            ? Math.round(scale.originalWidth)
            : undefined,
        mark_media_original_height: scale?.originalHeight
            ? Math.round(scale.originalHeight)
            : undefined,
        // Кастомные цвета для палитры
        // Преобразуем массив в строку с разделителем запятой
        mark_media_custom_text_colors:
            customTextColors && customTextColors.length > 0
                ? customTextColors.join(',')
                : undefined,
        mark_media_custom_background_colors:
            customBackgroundColors && customBackgroundColors.length > 0
                ? customBackgroundColors.join(',')
                : undefined,
    }
}
